import { productRealPrice } from "../price/price.util";
import { categoryNameProduct, namesVariant, skuProductItem } from "../product/product.util";
import { TrackTypes } from "./track";

declare const window: any;
declare const document: any;

export async function waitForGtagReady(maxWait = 3000): Promise<void> 
{
    return new Promise((resolve) => 
    {
        const interval = 50;
        let waited = 0;
  
        const timer = setInterval(() => 
        {
            if (typeof window !== "undefined" && window.__gtagInitialized) 
            {
                clearInterval(timer);
                resolve();
            }
  
            waited += interval;

            if (waited >= maxWait) 
            {
                clearInterval(timer);
                resolve(); // ainda resolve, mesmo que não tenha carregado
            }
        }, interval);
    });
}
  
const validateGaEvent = (event: TrackTypes, data: Record<string, any>): boolean => 
{
    switch (event) 
    {
        case TrackTypes.ViewContent:
        case TrackTypes.AddToCart:
        case TrackTypes.AddToWishlist:
            if (!data.product) 
            {
                console.error(`[GA Track] Missing product data for event: ${event}`);
                return false;
            }

            if (!data.product.name || (!data.product.code && !data.product.id)) 
            {
                console.error(`[GA Track] Missing product name or id/code for event: ${event}`);
                return false;
            }

            if (data.quantity !== undefined && (typeof data.quantity !== "number" || data.quantity < 0)) 
            {
                console.error(`[GA Track] Invalid quantity for event: ${event}`);
                return false;
            }

            return true;

        case TrackTypes.Search:
            if (!data.term || typeof data.term !== "string") 
            {
                console.error(`[GA Track] Missing or invalid search term for event: ${event}`);
                return false;
            }

            return true;

        case TrackTypes.PageView:
        case TrackTypes.CompleteRegistration:
        case TrackTypes.Lead:
            return true;

        case TrackTypes.InitiateCheckout:
        case TrackTypes.AddPaymentInfo:
            if (!data.cart || !Array.isArray(data.cart.items) || data.cart.items.length === 0) 
            {
                console.error(`[GA Track] Missing or empty cart items for event: ${event}`);
                return false;
            }

            if (typeof data.cart.total !== "number" || data.cart.total < 0) 
            {
                console.error(`[GA Track] Invalid cart total for event: ${event}`);
                return false;
            }

            return true;

        case TrackTypes.Purchase:
            if (!data.order || !Array.isArray(data.order.items) || data.order.items.length === 0) 
            {
                console.error(`[GA Track] Missing or empty order items for event: ${event}`);
                return false;
            }

            if (typeof data.order.total !== "number" || data.order.total < 0) 
            {
                console.error(`[GA Track] Invalid order total for event: ${event}`);
                return false;
            }

            if (!data.order.id) 
            {
                console.error("[GA Track] Missing order id for Purchase event");
                return false;
            }

            return true;

        default:
            return true;
    }
};

export const gaTrack = async (eventId: string, externalId:string, eventType: TrackTypes, data: Record<string, any> = {}) => 
{    
    if (!validateGaEvent(eventType, data)) return;

    await waitForGtagReady();

    if (typeof window === "undefined" || typeof window.gtag !== "function") return;

    let eventData  : any;
    let eventName : any;

    const commonItemData = (product: any, variant: any, quantity = 1) => ({
        item_id       : skuProductItem(product, variant),
        item_name     : product?.name,
        item_category : categoryNameProduct(product) || "Sem categoria",
        item_variant  : namesVariant(variant, "|", true) || "Padrão",
        price         : productRealPrice(product, variant, false),
        quantity,
    });

    switch (eventType) 
    {
        case TrackTypes.ViewContent:
        case TrackTypes.AddToCart:
        case TrackTypes.AddToWishlist:
            
            const quantity = data?.quantity || 1;
            eventName = eventType === TrackTypes.ViewContent ? "view_item" : eventType === TrackTypes.AddToCart ? "add_to_cart" : "add_to_wishlist";
            
            eventData = {
                referencePath : data?.product?.referencePath || "",
                currency      : "BRL",
                value         : productRealPrice(data?.product, data?.variant, false) * quantity,
                items         : [ commonItemData(data?.product, data?.variant, quantity) ],
                event_id      : eventId,
            };

            window.gtag(
                "event",
                eventName,
                eventData
            );
            break;

        case TrackTypes.Search:
            eventName = "search";
            eventData = {
                search_term : data?.term || "",
                event_id    : eventId,
                eventName   : eventName,
            };
            window.gtag("event", eventName, eventData);
            break;

        case TrackTypes.PageView:
            eventName = "page_view";
            eventData = {
                page_title    : typeof document !== "undefined" ? document.title : "",
                page_location : window.location.href,
                page_path     : window.location.pathname,
                event_id      : eventId,
            };
            window.gtag("event", eventName, eventData);
            break;

        case TrackTypes.CompleteRegistration:
        case TrackTypes.Lead:
            eventName = eventType === TrackTypes.CompleteRegistration ? "sign_up" : "lead";
            eventData = {
                method       : "email",
                content_name : "Cadastro no site",
                status       : "success",
                value        : 0,
                currency     : "BRL",
                event_id     : eventId,
            };
            window.gtag("event", eventName, eventData);
            break;

        case TrackTypes.InitiateCheckout:
        case TrackTypes.AddPaymentInfo:
            eventName = eventType === TrackTypes.InitiateCheckout ? "begin_checkout" : "add_payment_info";
            const checkoutItems = data?.cart?.items?.map((item: any) =>
                commonItemData(item, item.variant, item.quantity)
            ) || [];

            eventData = {
                items        : checkoutItems,
                value        : data?.cart?.total || 0,
                coupon       : data?.cart?.coupon?._code || "",
                payment_type : data?.cart?.paymentMethod?.value || "",
                currency     : "BRL",
                event_id     : eventId,
            };

            window.gtag(
                "event",
                eventName,
                eventData
            );
            break;

        case TrackTypes.Purchase:
            eventName = "purchase";
            const purchaseItems = data?.order?.items?.map((item: any) =>
                commonItemData(item, item.variant, item.quantity)
            ) || [];

            eventData = {
                items          : purchaseItems,
                value          : data?.order?.total || 0,
                shipping       : data?.order?.totalShipping || 0,
                coupon         : data?.order?.coupon?._code || "",
                payment_type   : data?.order?.paymentMethod?.value || "",
                currency       : "BRL",
                transaction_id : data?.order?.id || "",
                event_id       : eventId,
            };

            window.gtag("event", eventName, eventData);
            break;

        default:
            break;
    }

    if (!eventName || !eventData)
    {
        return;
    }

    return { eventName, eventData };
};